import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../config/api';

interface User {
  userId: string;
  role: string;
  permissions: string[] | string | any;
  created_at?: string;
  last_login?: string;
}

interface SystemUsage {
  system: string;
  lastUsed: number | null;
  lastUsedBy: string | null;
  note?: string;
  error?: string;
}

interface UsageLog {
  logId: string;
  userId: string;
  systemType: string;
  action: string;
  timestamp: number;
  fileName?: string;
  fileSize?: number;
  duration?: number;
  success: boolean;
  errorMessage?: string;
}

interface UserFormData {
  email: string;
  password: string;
  role: 'user' | 'admin';
  permissions: string[];
}

type SortField = 'timestamp' | 'userId' | 'systemType' | 'fileName';
type SortDirection = 'asc' | 'desc';

// Available services in the system.
// NOTE: these ids are FLAT tool names (e.g. 'urban_wiki'), not the hierarchical
// strings in backend/shared/permissions.py (e.g. 'urban_wiki:view'). That
// mismatch is real and is bridged in AuthContext.hasPermission, which treats a
// bare tool name as granting its actions — so granting 'urban_wiki' here
// satisfies the 'urban_wiki:view' gates on the Dashboard tile, TopBar entry and
// route. Keep this list flat unless you also change that bridge.
const AVAILABLE_SERVICES = [
  { id: 'walking_calculator', name: 'Promenadavstånd', description: 'Hitta platser inom gångavstånd' },
  { id: 'street_analyzer', name: 'Gatuanalys', description: 'Analysera gatumiljöer och fasader med AI' },
  { id: 'dp_analyzer', name: 'DP-Analys', description: 'Analysera detaljplaner för hälsofrämjande livsmiljöer' },
  { id: 'open_space', name: 'Tomma ytor', description: 'Hitta byggnadsfria ytor större än 45×45 m' },
  { id: 'space_syntax', name: 'Gatunät', description: 'Space syntax: integration och genomströmning i gatunätet' },
  { id: 'urbanitet', name: 'Urbanitetstal', description: 'Urbanitetstal per kvarter i Gatunät (Lantmäteriets fastigheter) – bara utvalda testare' },
  { id: 'urban_wiki', name: 'Urban Wiki', description: 'Tvåspråkig kunskapsbas och ordlista från forskningsdokument' },
];

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [systemUsage, setSystemUsage] = useState<SystemUsage[]>([]);
  const [usageLogs, setUsageLogs] = useState<UsageLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'users'>('overview');
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // User management state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [changingPasswordFor, setChangingPasswordFor] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    password: '',
    role: 'user',
    permissions: [],
  });
  const [formLoading, setFormLoading] = useState(false);


  const getSafePermissions = (user: User): string[] => {
    if (!user.permissions) return [];
    if (Array.isArray(user.permissions)) return user.permissions;
    if (typeof user.permissions === 'string') {
      try {
        const parsed = JSON.parse(user.permissions);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const clearMessages = () => {
    setError(null);
    setSuccessMessage(null);
  };

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  const fetchSystemUsage = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/usage`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSystemUsage(data.systems || []);
      }
    } catch (err) {
      console.error('Error fetching system usage:', err);
    }
  }, []);

  const fetchUsageLogs = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/usage-logs`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsageLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Error fetching usage logs:', err);
    }
  }, []);

  useEffect(() => {
    if (user) {
      setLoading(true);
      Promise.all([fetchUsers(), fetchSystemUsage(), fetchUsageLogs()])
        .finally(() => setLoading(false));
    }
  }, [user, fetchUsers, fetchSystemUsage, fetchUsageLogs]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedLogs = [...usageLogs].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'timestamp':
        comparison = a.timestamp - b.timestamp;
        break;
      case 'userId':
        comparison = a.userId.localeCompare(b.userId);
        break;
      case 'systemType':
        comparison = a.systemType.localeCompare(b.systemType);
        break;
      case 'fileName':
        comparison = (a.fileName || '').localeCompare(b.fileName || '');
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Aldrig';
    return new Date(timestamp).toLocaleString('sv-SE');
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    setFormLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: formData.email,
          password: formData.password,
          role: formData.role,
          permissions: formData.permissions,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(`Användare ${formData.email} skapad`);
        setShowCreateForm(false);
        setFormData({ email: '', password: '', role: 'user', permissions: [] });
        fetchUsers();
      } else {
        setError(data.message || 'Kunde inte skapa användare');
      }
    } catch (err) {
      setError('Nätverksfel vid skapande av användare');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    clearMessages();
    setFormLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${encodeURIComponent(editingUser.userId)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          role: formData.role,
          permissions: formData.permissions,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(`Användare ${editingUser.userId} uppdaterad`);
        setEditingUser(null);
        setFormData({ email: '', password: '', role: 'user', permissions: [] });
        fetchUsers();
      } else {
        setError(data.message || 'Kunde inte uppdatera användare');
      }
    } catch (err) {
      setError('Nätverksfel vid uppdatering av användare');
    } finally {
      setFormLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!changingPasswordFor) return;
    clearMessages();
    setFormLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${encodeURIComponent(changingPasswordFor)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(`Lösenord ändrat för ${changingPasswordFor}`);
        setChangingPasswordFor(null);
        setNewPassword('');
      } else {
        setError(data.message || 'Kunde inte ändra lösenord');
      }
    } catch (err) {
      setError('Nätverksfel vid ändring av lösenord');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm(`Är du säker på att du vill ta bort användaren ${userId}?`)) {
      return;
    }
    clearMessages();

    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setSuccessMessage(`Användare ${userId} borttagen`);
        fetchUsers();
      } else {
        const data = await response.json();
        setError(data.message || 'Kunde inte ta bort användare');
      }
    } catch (err) {
      setError('Nätverksfel vid borttagning av användare');
    }
  };

  const startEditingUser = (u: User) => {
    setEditingUser(u);
    setFormData({
      email: u.userId,
      password: '',
      role: u.role as 'user' | 'admin',
      permissions: getSafePermissions(u),
    });
    setShowCreateForm(false);
    setChangingPasswordFor(null);
    clearMessages();
  };

  const startCreatingUser = () => {
    setShowCreateForm(true);
    setEditingUser(null);
    setChangingPasswordFor(null);
    setFormData({ email: '', password: '', role: 'user', permissions: [] });
    clearMessages();
  };

  const startChangingPassword = (userId: string) => {
    setChangingPasswordFor(userId);
    setNewPassword('');
    setEditingUser(null);
    setShowCreateForm(false);
    clearMessages();
  };

  const cancelForm = () => {
    setShowCreateForm(false);
    setEditingUser(null);
    setChangingPasswordFor(null);
    setFormData({ email: '', password: '', role: 'user', permissions: [] });
    setNewPassword('');
    clearMessages();
  };

  const togglePermission = (permissionId: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId],
    }));
  };

  const getServiceName = (permissionId: string): string => {
    const service = AVAILABLE_SERVICES.find(s => s.id === permissionId);
    return service ? service.name : permissionId;
  };

  if (user?.role !== 'admin') {
    return (
      <div className="admin-panel">
        <div className="admin-error">
          <h2>Åtkomst nekad</h2>
          <p>Du har inte behörighet att visa denna sida.</p>
          <Link to="/dashboard" className="back-link" data-tip="admin.back">Tillbaka till Dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="page-header">
        <img src="/Maestro Basic Logo.png" alt="Maestro Logo" />
        <h2>Admin Panel</h2>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
          data-tip="admin.tab.overview"
        >
          Översikt
        </button>
        <button
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
          data-tip="admin.tab.users"
        >
          Användare
        </button>
      </div>

      {loading && <div className="loading">Laddar...</div>}
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {activeTab === 'overview' && (
        <div className="admin-section">
          <h3>Systemanvändning</h3>
          <div className="usage-grid">
            {systemUsage.map((system) => (
              <div key={system.system} className="usage-card">
                <h4>{system.system}</h4>
                <p>Senast använd: {formatDate(system.lastUsed)}</p>
                <p>Av: {system.lastUsedBy || '-'}</p>
                {system.note && <p className="note">{system.note}</p>}
              </div>
            ))}
          </div>

          <h3>Användningsloggar</h3>
          <div className="usage-logs-table">
            <table>
              <thead>
                <tr>
                  <th onClick={() => handleSort('timestamp')} className="sortable" data-tip="admin.sort">
                    Tidpunkt {sortField === 'timestamp' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('userId')} className="sortable" data-tip="admin.sort">
                    Användare {sortField === 'userId' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('systemType')} className="sortable" data-tip="admin.sort">
                    System {sortField === 'systemType' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Åtgärd</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sortedLogs.slice(0, 50).map((log) => (
                  <tr key={log.logId} className={log.success ? '' : 'error-row'}>
                    <td>{formatDate(log.timestamp)}</td>
                    <td>{log.userId}</td>
                    <td>{log.systemType}</td>
                    <td>{log.action}</td>
                    <td>{log.success ? 'OK' : 'Fel'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="admin-section">
          <div className="section-header">
            <h3>Användare ({users.length})</h3>
            <button
              className="primary-btn"
              onClick={startCreatingUser}
              disabled={showCreateForm}
              data-tip="admin.new"
            >
              + Ny användare
            </button>
          </div>

          {/* Create User Form */}
          {showCreateForm && (
            <div className="user-form-container">
              <h4>Skapa ny användare</h4>
              <form onSubmit={handleCreateUser} className="user-form">
                <div className="form-group">
                  <label>E-post</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="anvandare@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Lösenord</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={8}
                    placeholder="Minst 8 tecken"
                  />
                </div>
                <div className="form-group">
                  <label>Roll</label>
                  <select
                    data-tip="admin.role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as 'user' | 'admin' })}
                  >
                    <option value="user">Användare</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Tjänster</label>
                  <div className="permissions-list">
                    {AVAILABLE_SERVICES.map((service) => (
                      <label key={service.id} className="permission-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.permissions.includes(service.id)}
                          onChange={() => togglePermission(service.id)}
                        />
                        <span className="permission-name">{service.name}</span>
                        <span className="permission-desc">{service.description}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="primary-btn" disabled={formLoading} data-tip="admin.create">
                    {formLoading ? 'Skapar...' : 'Skapa användare'}
                  </button>
                  <button type="button" className="secondary-btn" onClick={cancelForm} data-tip="admin.cancel">
                    Avbryt
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Edit User Form */}
          {editingUser && (
            <div className="user-form-container">
              <h4>Redigera användare: {editingUser.userId}</h4>
              <form onSubmit={handleUpdateUser} className="user-form">
                <div className="form-group">
                  <label>Roll</label>
                  <select
                    data-tip="admin.role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as 'user' | 'admin' })}
                  >
                    <option value="user">Användare</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Tjänster</label>
                  <div className="permissions-list">
                    {AVAILABLE_SERVICES.map((service) => (
                      <label key={service.id} className="permission-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.permissions.includes(service.id)}
                          onChange={() => togglePermission(service.id)}
                        />
                        <span className="permission-name">{service.name}</span>
                        <span className="permission-desc">{service.description}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="primary-btn" disabled={formLoading} data-tip="admin.save">
                    {formLoading ? 'Sparar...' : 'Spara ändringar'}
                  </button>
                  <button type="button" className="secondary-btn" onClick={cancelForm} data-tip="admin.cancel">
                    Avbryt
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Change Password Form */}
          {changingPasswordFor && (
            <div className="user-form-container">
              <h4>Ändra lösenord för: {changingPasswordFor}</h4>
              <form onSubmit={handleChangePassword} className="user-form">
                <div className="form-group">
                  <label>Nytt lösenord</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    placeholder="Minst 8 tecken"
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="primary-btn" disabled={formLoading} data-tip="admin.password">
                    {formLoading ? 'Ändrar...' : 'Ändra lösenord'}
                  </button>
                  <button type="button" className="secondary-btn" onClick={cancelForm} data-tip="admin.cancel">
                    Avbryt
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Users Table */}
          <div className="users-table">
            <table>
              <thead>
                <tr>
                  <th>E-post</th>
                  <th>Roll</th>
                  <th>Tjänster</th>
                  <th>Skapad</th>
                  <th>Senast inloggad</th>
                  <th>Åtgärder</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.userId}>
                    <td>{u.userId}</td>
                    <td>
                      <span className={`role-badge ${u.role}`}>
                        {u.role === 'admin' ? 'Admin' : 'Användare'}
                      </span>
                    </td>
                    <td>
                      {getSafePermissions(u).map(p => getServiceName(p)).join(', ') || '-'}
                    </td>
                    <td>{u.created_at ? new Date(u.created_at).toLocaleDateString('sv-SE') : '-'}</td>
                    <td>{u.last_login ? new Date(u.last_login).toLocaleString('sv-SE') : '-'}</td>
                    <td className="actions-cell">
                      <button
                        className="action-btn edit"
                        onClick={() => startEditingUser(u)}
                        data-tip="admin.edit"
                      >
                        Redigera
                      </button>
                      <button
                        className="action-btn password"
                        onClick={() => startChangingPassword(u.userId)}
                        data-tip="admin.password"
                      >
                        Lösenord
                      </button>
                      {u.userId !== user?.userId && (
                        <button
                          className="action-btn delete"
                          onClick={() => handleDeleteUser(u.userId)}
                          data-tip="admin.delete"
                        >
                          Ta bort
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="admin-footer">
        <Link to="/dashboard" className="back-link" data-tip="admin.back">Tillbaka till Dashboard</Link>
      </div>
    </div>
  );
};

export default AdminPanel;
